import type { HTMLAttributes, ReactNode, Ref } from 'react';

type TypographyProps = HTMLAttributes<HTMLElement> & {
  children: ReactNode;
  ref?: Ref<HTMLElement>;
};

/**
 * @param children - Content
 * @param as - Element to render; `p` or `div` for display-sized type that isn't the page heading
 * @returns Display-level type in Jeffesta (renders h1 by default)
 */
export function Display({ className = '', children, ref, as: Tag = 'h1', ...props }: TypographyProps & { as?: 'h1' | 'p' | 'div' }) {
  return (
    <Tag ref={ref as Ref<HTMLHeadingElement & HTMLParagraphElement & HTMLDivElement>} className={`font-display text-5xl tracking-[1px] text-maroon-900 md:text-7xl lg:text-8xl ${className}`} {...props}>
      {children}
    </Tag>
  );
}

/**
 * @param children - Content
 * @returns H1-level heading in Jeffesta (renders h1)
 */
export function H1({ className = '', children, ref, ...props }: TypographyProps) {
  return (
    <h1 ref={ref as Ref<HTMLHeadingElement>} className={`font-display text-4xl tracking-[1px] text-maroon-900 md:text-5xl lg:text-6xl ${className}`} {...props}>
      {children}
    </h1>
  );
}

/**
 * @param children - Content
 * @returns H2-level heading in Jeffesta (renders h2)
 */
export function H2({ className = '', children, ref, ...props }: TypographyProps) {
  return (
    <h2 ref={ref as Ref<HTMLHeadingElement>} className={`font-display text-3xl tracking-[1px] text-maroon-900 md:text-4xl ${className}`} {...props}>
      {children}
    </h2>
  );
}

/**
 * @param children - Content
 * @returns H3-level heading in Jeffesta (renders h3)
 */
export function H3({ className = '', children, ref, ...props }: TypographyProps) {
  return (
    <h3 ref={ref as Ref<HTMLHeadingElement>} className={`font-display text-xl tracking-[1px] text-maroon-900 md:text-2xl ${className}`} {...props}>
      {children}
    </h3>
  );
}

/**
 * @param children - Content
 * @returns Large body text for intros and leads (renders p)
 */
export function BodyLarge({ className = '', children, ref, ...props }: TypographyProps) {
  return (
    <p ref={ref as Ref<HTMLParagraphElement>} className={`max-w-[65ch] text-lg leading-relaxed text-maroon-800 ${className}`} {...props}>
      {children}
    </p>
  );
}

/**
 * @param children - Content
 * @returns Standard body text (renders p)
 */
export function Body({ className = '', children, ref, ...props }: TypographyProps) {
  return (
    <p ref={ref as Ref<HTMLParagraphElement>} className={`max-w-[65ch] text-base leading-relaxed text-maroon-800 ${className}`} {...props}>
      {children}
    </p>
  );
}

/**
 * @param children - Content
 * @returns Small text for captions and metadata (renders p)
 */
export function Small({ className = '', children, ref, ...props }: TypographyProps) {
  return (
    <p ref={ref as Ref<HTMLParagraphElement>} className={`text-sm text-maroon-600 ${className}`} {...props}>
      {children}
    </p>
  );
}

/**
 * @param children - Content
 * @returns Label text, uppercase and tracked (renders span)
 */
export function Label({ className = '', children, ref, ...props }: HTMLAttributes<HTMLSpanElement> & { children: ReactNode; ref?: Ref<HTMLSpanElement> }) {
  return (
    <span ref={ref} className={`text-xs font-semibold uppercase tracking-widest text-maroon-400 ${className}`} {...props}>
      {children}
    </span>
  );
}
